// ============================================================================
// TimelineLegend.jsx — what every mark on the shift timeline means.
//
// The bar carries six mark types, five site types and one state, which is more
// than a reader can be expected to infer. Before this existed the only way to
// learn that a violet box meant "clock-in to yard departure" was to click one,
// and the ticks were not clickable at all.
//
// Two rules this follows, both from the project's chart guidance:
//
//  - Identity is never colour alone. Every entry pairs its swatch with a text
//    label, and the two ends differ in POSITION on the axis as well as in hue,
//    so a reader who cannot separate violet from blue still has the layout.
//  - Over threshold is a STATE, not a class. It sits in its own group rather
//    than as a another identity hue, because red at either end means the same
//    thing — over the line for that end — while violet and blue mean which end
//    you are looking at.
//
// ── Why the entries are grouped ─────────────────────────────────────────────
// Thirteen entries in one flat row wrapped to two lines and read as a wall.
// Grouping them lets the group carry the shared half of each name, so
// "Site — third party disposal" becomes "third party disposal" under Sites and
// "Meal gap — ambiguous" becomes "Ambiguous" under Meal gap. Same information,
// about a third of the text, and the reader can skip a whole group at a glance.
//
// The thresholds are passed in rather than hardcoded: they are provisional,
// they differ per end (45 close-out, 60 start), and the legend is exactly the
// place a reader would notice if it said something the tiles did not.
// ============================================================================

import { c, t, sp, r, chart } from '../../theme';
import { visitColor, siteTypeLabel } from './timeline';

function Swatch({ kind, colour }) {
  if (kind === 'tick') return <span style={{ ...s.tick, background: colour }} />;
  if (kind === 'box') {
    return <span style={{ ...s.box, borderColor: colour, background: colour === c.danger
      ? chart.overFill
      : colour === chart.shiftStart ? chart.shiftStartFill : chart.shiftEndFill }} />;
  }
  if (kind === 'outline') {
    return <span style={{ ...s.chip, background: c.warningTint, border: `1px solid ${c.warning}` }} />;
  }
  if (kind === 'chip') return <span style={{ ...s.chip, background: colour }} />;
  return <span style={{ ...s.bar, background: c.successTint, border: `1px solid ${c.success}33` }} />;
}

// Ordered by how often they occur, so the common case reads first: yards are
// 89% of visits and `other` is the tail the palette folds into a neutral.
const SITE_TYPES = ['company_yard', 'company_landfill', 'third_party_disposal',
                    'fueling', 'other'];

export default function TimelineLegend({ thresholdMin = 45, preThresholdMin = 60 }) {
  return (
    <div style={s.wrap} aria-label="Timeline legend">
      <Group title="Shift">
        <Item kind="bar"  label="Worked" />
        <Item kind="box"  colour={chart.shiftStart} label="Clock-in → yard departure" />
        <Item kind="tick" colour={chart.shiftStart} label="Pre-trip inspection" />
        <Item kind="box"  colour={chart.shiftEnd}   label="Yard arrival → punch-out" />
        <Item kind="tick" colour={chart.shiftEnd}   label="Post-trip inspection" />
      </Group>

      <Group title="Meal gap">
        <Item kind="chip"    colour={c.danger} label="Unambiguous" />
        <Item kind="outline"                   label="Ambiguous" />
      </Group>

      {/* Five site types share one lane and are compared against each other,
          so every one is named — colour alone cannot carry identity here any
          more than it can above. `other` is a neutral rather than a fifth hue,
          because coVisible has four all-pairs-validated slots and inventing a
          fifth is the thing CLAUDE.md tells you not to do. */}
      <Group title="Sites">
        {SITE_TYPES.map((st) => (
          <Item key={st} kind="chip" colour={visitColor(chart, st)} label={siteTypeLabel(st)} />
        ))}
      </Group>

      <Group title="Flagged">
        <Item kind="box" colour={c.danger}
              label={`Over ${preThresholdMin}m start · ${thresholdMin}m close-out`} />
      </Group>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <span style={s.group}>
      <span style={s.groupTitle}>{title}</span>
      {children}
    </span>
  );
}

function Item({ kind, colour, label }) {
  return (
    <span style={s.item}>
      <Swatch kind={kind} colour={colour} />
      <span style={s.label}>{label}</span>
    </span>
  );
}

const s = {
  // sp is unit-bearing ('4px'), so these are the raw tokens. Writing
  // `${sp[1]}px` yields "4pxpx", which the browser drops silently — that is
  // what left this row with no gap and no padding, the swatches touching the
  // label before them and the last entry clipped.
  wrap: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center',
    gap: `${sp[2]} ${sp[4]}`,
    padding: `${sp[2]} ${sp[3]}`, borderBottom: `1px solid ${c.border}`,
    background: c.surfaceSunk,
  },
  // Groups never break internally: a swatch stranded from its label, or half a
  // group on the next line, is worse than the row being one line taller.
  group: {
    display: 'inline-flex', alignItems: 'center', gap: sp[2],
    flexWrap: 'nowrap', whiteSpace: 'nowrap',
  },
  groupTitle: {
    ...t.micro, fontWeight: 700, color: c.textFaint,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    paddingRight: sp[1], borderRight: `1px solid ${c.border}`,
  },
  item:  { display: 'inline-flex', alignItems: 'center', gap: sp[1], whiteSpace: 'nowrap' },
  label: { ...t.micro, color: c.textMuted },
  // The swatches echo the marks' own geometry, not generic squares: a reader
  // matches shape first and colour second, which is the point.
  bar:  { width: 22, height: 10, borderRadius: r.sm, display: 'inline-block', flexShrink: 0 },
  box:  { width: 22, height: 12, borderRadius: r.sm, borderWidth: 1.5, borderStyle: 'solid',
          display: 'inline-block', flexShrink: 0 },
  tick: { width: 2, height: 13, borderRadius: 1, display: 'inline-block', flexShrink: 0 },
  chip: { width: 12, height: 8, borderRadius: 2, display: 'inline-block', flexShrink: 0 },
};
